/**
 * A página comercial da IA (E12).
 *
 * ── O que ela faz, e o que não faz ────────────────────────────────────────
 *
 * Apresenta o recurso e leva quem se interessou ao comercial. Ativar é uma
 * conversa com uma pessoa, e por isso a tela NÃO tem botão que ligue a IA,
 * nem campo, nem nada que simule o recurso: a única ação é o convite logo
 * abaixo da manchete, que chama o comercial no WhatsApp.
 *
 * Sem "em breve" em lugar nenhum, por decisão do dono do produto (29/09/2026).
 * O teste de acessibilidade reprova se a expressão voltar.
 *
 * Sem número configurado, o convite some: chamar para ativar sem ter como
 * ativar seria beco sem saída.
 *
 * ── A cena é decorativa ───────────────────────────────────────────────────
 *
 * O lead e a anotação da ilustração são inventados, e por isso ela é
 * `aria-hidden`: quem usa leitor de tela ouviria "Camilli, Proposta enviada,
 * Alta" como se fosse um cliente da base. O que a cena mostra está dito em
 * texto nos recursos logo abaixo, e é por ali que essa pessoa chega.
 *
 * ── O movimento ───────────────────────────────────────────────────────────
 *
 * Mora inteiro em `ia.css`: um ciclo só para a cena toda, e com
 * `prefers-reduced-motion` tudo para no estado final.
 */

import { useEffect, type ReactNode } from 'react';
import { Icone, type NomeDoIcone } from '@/shared/ui';
import './ia.css';

/** Exportado para o teste de acessibilidade conferir a aba. */
export const TITULO_DA_ABA = 'IA';

/**
 * O WhatsApp do comercial, que o convite do topo chama.
 *
 * Só dígitos, com o país — o formato do `wa.me`. Definido pelo dono do produto
 * (29/09/2026). Vazio esconde o convite, como a linha de WhatsApp em
 * `widgets/entrada/config/suporte.ts`.
 */
export const WHATSAPP_COMERCIAL = '559831909970';

/*
 * A mensagem já escrita na conversa. Sem nome de pessoa nem de organização:
 * ela vai na URL, e a URL passa pelos servidores do WhatsApp antes de a pessoa
 * decidir enviar. Quem é quem o comercial pergunta na conversa.
 */
const MENSAGEM_AO_COMERCIAL = 'Olá! Quero ativar a IA do CliqCRM na minha organização.';

const RECURSOS: readonly { icone: NomeDoIcone; titulo: string; descricao: string }[] = [
  {
    icone: 'lupa',
    titulo: 'Análise de leads',
    descricao:
      'Lê o histórico, as conversas e a etapa do funil para mostrar quem merece atenção agora.',
  },
  {
    icone: 'lapis',
    titulo: 'Anotações melhores',
    descricao:
      'Você escreve do seu jeito, rápido. A IA organiza, corrige e deixa claro o próximo passo.',
  },
  {
    icone: 'seta',
    titulo: 'Próximos passos sugeridos',
    descricao: 'Recebe lembretes de quando ligar, o que responder e quais leads estão esfriando.',
  },
  {
    icone: 'atendimento',
    titulo: 'Respostas aos seus clientes',
    descricao:
      'A IA responde seus clientes com base no histórico da conversa e passa o atendimento para você quando for a hora.',
  },
];

export function TelaDaIa({
  whatsapp = WHATSAPP_COMERCIAL,
}: {
  /** Só dígitos, com o país. Vazio esconde o convite. */
  whatsapp?: string;
}): ReactNode {
  /*
   * O título da aba volta ao que era ao sair.
   *
   * Nenhuma outra tela mexe nele, então sem a devolução a aba continuaria
   * dizendo "IA" com o funil aberto.
   */
  useEffect(() => {
    const anterior = document.title;
    document.title = TITULO_DA_ABA;
    return () => {
      document.title = anterior;
    };
  }, []);

  return (
    <div className="ia">
      <div className="ia__pagina">
        <section className="ia__heroi" aria-labelledby="ia-titulo">
          <div className="ia__texto">
            <h1 className="ia__titulo" id="ia-titulo">
              Uma IA que lê seus leads e escreve junto com você
            </h1>
            <p className="ia__descricao">
              A IA analisa cada lead, mostra quem está mais perto de fechar e transforma suas
              anotações rápidas em registros claros e organizados.
            </p>

            {/*
              O convite fica AQUI, junto da manchete, e não no fim da página:
              no fim ele só era visto por quem rolasse até lá, e passava
              despercebido. O ícone é o do WhatsApp para a pessoa saber, antes
              de clicar, que vai cair numa conversa, e a nota diz o mesmo para
              quem usa leitor de tela, pelo `aria-describedby`.
            */}
            {whatsapp && (
              <div className="ia__convite">
                <a
                  className="bt bt--primario ia__ativar"
                  href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(MENSAGEM_AO_COMERCIAL)}`}
                  target="_blank"
                  // `noreferrer` também implica `noopener`, como nos links da conversa.
                  rel="noreferrer"
                  aria-describedby="ia-convite-nota"
                >
                  <Icone nome="whatsapp" />
                  Quero ativar a IA
                </a>
                <p className="ia__convite-nota" id="ia-convite-nota">
                  Chame o nosso time no WhatsApp para ativar a IA na sua organização.
                </p>
              </div>
            )}
          </div>

          <Cena />
        </section>

        <ul className="ia__recursos">
          {RECURSOS.map((recurso) => (
            <li key={recurso.titulo} className="ia__recurso">
              <span className="ia__recurso-icone" aria-hidden="true">
                <Icone nome={recurso.icone} />
              </span>
              <h2 className="ia__recurso-titulo">{recurso.titulo}</h2>
              <p className="ia__recurso-descricao">{recurso.descricao}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * A ilustração: o lead sendo lido e a anotação sendo reescrita.
 *
 * Duas caixas, e não uma: `.ia__cena` é o que ocupa lugar no layout, e
 * `.ia__ilustracao` é o desenho de 440×400. No celular o desenho encolhe por
 * `transform`, que não muda a caixa — e é a cena, reduzida junto, que impede a
 * largura original de empurrar a página para o lado.
 */
function Cena(): ReactNode {
  return (
    <div className="ia__cena" aria-hidden="true">
      <div className="ia__ilustracao">
        <div className="ia__selo">
          <span className="ia__anel" />
          <span className="ia__disco">
            <span className="ia__estrela">
              <Icone nome="ia" />
            </span>
          </span>
        </div>

        <div className="ia__cartao ia__cartao--lead">
          <span className="ia__varredura" />
          <div className="ia__pessoa">
            <span className="ia__avatar">C</span>
            <div className="ia__quem">
              <b className="ia__nome">Camilli</b>
              <span className="ia__etapa">Etapa: Proposta enviada</span>
            </div>
          </div>
          <div className="ia__chance">
            <span>Chance de fechar</span>
            <b>Alta</b>
          </div>
          <div className="ia__barra">
            <span className="ia__preenchimento" />
          </div>
          <div className="ia__tags">
            <span className="ia__tag ia__tag--verde">Responde rápido</span>
            <span className="ia__tag ia__tag--azul">Pediu proposta 2x</span>
            <span className="ia__tag ia__tag--roxo">Sugestão: ligar hoje</span>
          </div>
        </div>

        <div className="ia__cartao ia__cartao--nota">
          <span className="ia__rotulo">
            <Icone nome="lapis" />
            Anotação
          </span>
          <span className="ia__rascunho">cliente achou caro, ver desc, ligar sexta</span>
          <div className="ia__melhorada">
            <span className="ia__carimbo">
              <Icone nome="ia" />
              Melhorada pela IA
            </span>
            <p className="ia__linhas">
              <span className="ia__linha">A cliente considerou o valor alto.</span>
              <span className="ia__linha">Próximo passo: avaliar um desconto</span>
              <span className="ia__linha">e retornar na sexta-feira.</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
